import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";
import { Icon, IconProps } from "../Icon";
import styles from "./Button.module.scss";
import { ButtonAppearance, LayoutUtilProps, Size } from "../../types/props";
import { Spinner } from "../Spinner";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the Button component
 * @extends Omit<ComponentPropsWithoutRef<"button">, "children">
 * @extends LayoutUtilProps
 */
export type ButtonProps = Omit<ComponentPropsWithoutRef<"button">, "children"> &
  LayoutUtilProps & {
    /**
     * The visual variant of the button.
     * @default secondary
     */
    appearance?: ButtonAppearance;
    /**
     * The size of the button.
     * @default medium
     */
    size?: Extract<Size, "small" | "medium" | "large">;

    /**
     * The loading state of the button.
     * If true, it will show infinite state of the loading.
     * @default false
     */
    loading?: boolean;
  } & (
    | {
        children?: ComponentPropsWithoutRef<"button">["children"];
        /**
         * The icons of the button.
         */
        icon?:
          | IconProps["svg"]
          | {
              after: IconProps["svg"];
            }
          | {
              before: IconProps["svg"];
            };
      }
    | {
        children?: never;
        /**
         * The icon of the icon only button.
         */
        icon?: IconProps["svg"];
      }
  );

/**
 * Button component for triggering actions and user interactions.
 *
 * Features:
 * - Multiple visual appearances (primary, secondary, ghost, danger variants)
 * - Three size options (small, medium, large)
 * - Loading state with spinner animation
 * - Icon support (before, after, or icon-only)
 * - Full accessibility support with ARIA attributes
 * - Supports layout utilities for positioning and spacing
 * - Automatic disabled state when loading
 * - Flexible content with text and/or icons
 *
 * @example
 * <Button appearance="primary" size="medium" onClick={() => console.log('clicked')}>
 *   Click Me
 * </Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      className,
      appearance = "secondary",
      size = "medium",
      icon,
      loading = false,
      disabled,
      style,
      type = "button",
      ...rest
    } = componentProps;

    const buttonClassNames = cx(className, styles["button"], {
      [styles["appearance-primary"]]: appearance === "primary",
      [styles["appearance-secondary"]]: appearance === "secondary",
      [styles["appearance-ghost"]]: appearance === "ghost",
      [styles["danger-secondary"]]: appearance === "danger-secondary",
      [styles["danger-primary"]]: appearance === "danger",
      [styles["size-small"]]: size === "small",
      [styles["size-medium"]]: size === "medium",
      [styles["size-large"]]: size === "large",
      [styles["type-icon"]]: !children,
      [styles["loading"]]: loading,
    });

    const iconBefore = (icon: ButtonProps["icon"]) => {
      if (typeof icon === "object" && "before" in icon) {
        return (
          <Icon
            className={styles["icon"]}
            inherit
            aria-hidden
            svg={icon.before}
          />
        );
      }
      if (icon && typeof icon !== "object") {
        return (
          <Icon
            className={styles["icon"]}
            inherit
            aria-hidden
            svg={icon}
            size={!children && size !== "small" ? "large" : "medium"}
          />
        );
      }
      return;
    };

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <button
        className={buttonClassNames}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading ? true : undefined}
        data-anv="button"
        style={styleCombined}
        ref={ref}
        {...rest}
      >
        {loading ? (
          <Spinner inherit className={styles["loading-spinner"]} />
        ) : icon ? (
          iconBefore(icon)
        ) : null}
        {children}
        {typeof icon === "object" && "after" in icon ? (
          <Icon
            className={styles["icon"]}
            inherit
            aria-hidden
            svg={icon.after}
          />
        ) : null}
      </button>
    );
  },
);

Button.displayName = "Button";
